import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton } from "@/hq/HQPrimitives";

const CUR_SYM = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const money = (minor, cur) => `${CUR_SYM[cur] || cur + " "}${(Number(minor || 0) / 100).toFixed(0)}`;

const TABS = [
  { key: "all",        label: "All" },
  { key: "optimise",   label: "Orange Optimise" },
  { key: "orangecare", label: "OrangeCare" },
  { key: "upcoming",   label: "Upcoming Payments" },
  { key: "renewals",   label: "Renewals" },
  { key: "cancelled",  label: "Cancelled" },
];

function Metric({ label, value, tone = "orange", testId }) {
  const toneMap = { orange: "text-[#FF6A00]", green: "text-[#0FA958]", red: "text-[#C1350B]", slate: "text-[#0A0A0A]" };
  return (
    <Card className="p-4" data-testid={testId}>
      <div className={`text-[10px] tracking-[0.28em] uppercase font-bold ${toneMap[tone]}`}>{label}</div>
      <div className="mt-2 font-black text-2xl tracking-[-0.02em] break-words">{value ?? "—"}</div>
    </Card>
  );
}

function byCur(obj) {
  if (!obj || !Object.keys(obj).length) return "—";
  return Object.entries(obj).map(([c, v]) => money(v, c)).join(" · ");
}

export default function HQSubscriptions() {
  const [tab, setTab] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    hqApi.get("/control/subscriptions", { params: { tab, page: 1, page_size: 100 } })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData({ items: [], metrics: {} }));
    return () => { alive = false; };
  }, [tab]);

  const m = data?.metrics;
  return (
    <div className="space-y-5" data-testid="hq-subscriptions">
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Subscriptions</div>
        <h1 className="mt-1 font-black text-2xl md:text-3xl tracking-[-0.02em]">Orange Optimise &amp; OrangeCare</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <Metric label="Total subscribers" value={m?.total_subscribers ?? "—"} testId="hq-subs-total"/>
        <Metric label="Monthly" value={m?.monthly_subscribers ?? "—"} testId="hq-subs-monthly"/>
        <Metric label="Annual" value={m?.annual_subscribers ?? "—"} testId="hq-subs-annual"/>
        <Metric label="Optimise users" value={m?.optimise_users ?? "—"} testId="hq-subs-optimise"/>
        <Metric label="OrangeCare users" value={m?.orangecare_users ?? "—"} testId="hq-subs-care"/>
        <Metric label="MRR" value={byCur(m?.mrr_by_currency)} tone="green" testId="hq-subs-mrr"/>
        <Metric label="ARR (est.)" value={byCur(m?.arr_by_currency)} tone="green" testId="hq-subs-arr"/>
        <Metric label="Payments due" value={m?.payments_due ?? "—"} testId="hq-subs-payments-due"/>
        <Metric label="Renewals due" value={m?.renewals_due ?? "—"} testId="hq-subs-renewals-due"/>
        <Metric label="Cancelled" value={m?.cancelled ?? "—"} tone="slate" testId="hq-subs-cancelled"/>
      </div>

      <div className="flex gap-2 overflow-x-auto" data-testid="hq-subs-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`hq-subs-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase whitespace-nowrap ${
              tab === t.key ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA] hover:bg-[#F6F5F2]"
            }`}
          >{t.label}</button>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.18em] uppercase font-bold text-[#8A8A8A]">
              <tr>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Interval</th>
                <th className="text-left px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Provider</th>
                <th className="text-left px-4 py-3">Next payment</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {!data ? (
                [0, 1, 2].map((i) => <tr key={i} className="border-t border-[#EAEAEA]"><td className="px-4 py-3" colSpan={8}><Skeleton h={20}/></td></tr>)
              ) : data.items?.length === 0 ? (
                <tr><td colSpan={8}><EmptyState title="No subscriptions" hint="Subscriptions will appear here as customers activate Optimise or OrangeCare." testId="hq-subs-empty"/></td></tr>
              ) : (
                data.items.map((s) => (
                  <tr key={s.id} className="border-t border-[#EAEAEA]" data-testid={`hq-subs-row-${s.id}`}>
                    <td className="px-4 py-3">
                      {s.business ? (
                        <Link to={`/hq/clients/${s.business.id}`} className="font-semibold hover:text-[#FF6A00]">{s.business.name}</Link>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3">{s.product === "orange_optimise" ? "Orange Optimise" : s.product === "orangecare" ? "OrangeCare" : "Other"}</td>
                    <td className="px-4 py-3">{s.plan?.name || "—"}</td>
                    <td className="px-4 py-3 uppercase text-[11px] tracking-[0.14em]">{s.billing_interval || "—"}</td>
                    <td className="px-4 py-3">{money(s.amount_minor, s.currency)}</td>
                    <td className="px-4 py-3">{s.provider}</td>
                    <td className="px-4 py-3">{s.next_billing_at ? new Date(s.next_billing_at).toLocaleDateString() : (s.renewal_at ? new Date(s.renewal_at).toLocaleDateString() : "—")}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] font-black tracking-[0.16em] uppercase ${s.status === "active" ? "text-[#0FA958]" : s.status === "cancelled" ? "text-[#8A8A8A]" : "text-[#8A6D00]"}`}>{s.status}</span></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="text-[12px] text-[#8A8A8A]">
        Deep-link into legacy screens: <Link to="/hq/optimise/subscriptions" className="text-[#FF6A00] hover:underline">Optimise commercial detail</Link> ·{" "}
        <Link to="/hq/optimise/monitoring" className="text-[#FF6A00] hover:underline">Monitoring profiles</Link>
      </div>
    </div>
  );
}
